/**
 * api.ts — GoPal
 * ─────────────────────────────────────────────────────────────────
 * Brain/skin separation: this is the ONLY file that knows the
 * backend exists. No component ever calls fetch or Supabase directly.
 * ─────────────────────────────────────────────────────────────────
 */

import type {
  ConnectionStatus, ApiResponse, OrgConfig, ConfigData, OrderStatus,
  Order, InventoryData, InventoryItem, Vendor,
  OrderFilters, PaginatedOrdersResponse,
  AnalyticsFilters, AnalyticsResponse,
  AdminOrgSummary, AdminOrgUser,
} from './types';

// ─── Auth token — injected by App.tsx via setAuthToken() ─────────
let _jwt: string | null = null;

export function setAuthToken(token: string | null): void {
  _jwt = token;
}

let _onAuthExpired: (() => void) | null = null;
export function setOnAuthExpired(cb: () => void): void {
  _onAuthExpired = cb;
}

async function getJwt(): Promise<string | null> {
  return _jwt;
}

// ─── Connection status observable ────────────────────────────────

type StatusListener = (status: ConnectionStatus) => void;
const _listeners = new Set<StatusListener>();
let _currentStatus: ConnectionStatus = 'connecting';

export const connectionStatus = {
  get(): ConnectionStatus { return _currentStatus; },
  subscribe(fn: StatusListener): () => void {
    _listeners.add(fn);
    fn(_currentStatus);
    return () => _listeners.delete(fn);
  },
  _set(s: ConnectionStatus) {
    if (s === _currentStatus) return;
    _currentStatus = s;
    _listeners.forEach(fn => fn(s));
  },
};

// ─── Error translation ──────────────────────────────────────────

const ERROR_MESSAGES: Record<string, string> = {
  UNAUTHORIZED:    'Please sign in again',
  FORBIDDEN:       'You don\'t have permission to do this',
  NO_PROFILE:      'Your account isn\'t set up yet. Contact your admin.',
  MISSING_FIELDS:  'Please fill in all required fields',
  USER_EXISTS:     'This email is already registered',
  DUPLICATE_VENDOR: 'A vendor with this domain already exists',
  INVALID_STATUS:  'Invalid status value',
  INVALID_DEPARTMENT: 'Invalid department selected',
  SERVER_ERROR:    'Something went wrong. Please try again.',
  NOT_AUTHENTICATED: 'Please sign in again',
  SESSION_EXPIRED: 'Your session has expired. Please sign in again.',
  DUPLICATE_CATEGORY: "This category name already exists — choose a different name.",
  VERSION_CONFLICT: 'This order was updated by someone else. Please refresh and try again.',
  TRIAL_EXPIRED:   'Your trial has ended. Contact hello@getgopal.co.nz to activate your subscription.',
};

function translateError(error: string | undefined): string {
  if (!error) return 'Something went wrong. Please try again.';
  if (ERROR_MESSAGES[error]) return ERROR_MESSAGES[error];
  if (error.startsWith('HTTP ')) return 'Something went wrong. Please try again.';
  if (error === 'Network error' || error === 'Failed to fetch') return 'Network error. Check your connection and try again.';
  return error;
}

// ─── Internal fetch helper ───────────────────────────────────────

const BASE    = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;
const SLOW_MS = 4000;

async function apiFetch<T>(
  path: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const jwt = await getJwt();
  if (!jwt) {
    connectionStatus._set('offline');
    return { ok: false, error: 'Not authenticated', message: translateError('NOT_AUTHENTICATED') };
  }

  const slowTimer = setTimeout(() => connectionStatus._set('slow'), SLOW_MS);

  try {
    const res = await fetch(`${BASE}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwt}`,
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
        ...(options.headers ?? {}),
      },
    });

    clearTimeout(slowTimer);

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const rawError = body?.error ?? `HTTP ${res.status}`;
      connectionStatus._set('connected');
      if (res.status === 401 && _onAuthExpired) { _onAuthExpired(); }
      return { ok: false, error: rawError, message: res.status === 401 ? translateError('SESSION_EXPIRED') : (body?.message ?? translateError(rawError)) };
    }

    connectionStatus._set('connected');
    return await res.json() as ApiResponse<T>;
  } catch (err) {
    clearTimeout(slowTimer);
    connectionStatus._set('offline');
    const rawError = err instanceof Error ? err.message : 'Network error';
    return { ok: false, error: rawError, message: translateError(rawError) };
  }
}

// ─── CONFIG ──────────────────────────────────────────────────────

export async function getConfig(): Promise<ApiResponse<ConfigData>> {
  return apiFetch<ConfigData>('/config');
}

export async function updateConfig(
  updates: Partial<OrgConfig>
): Promise<ApiResponse<{ message: string }>> {
  const flatUpdates: Record<string, string> = {};
  if (updates.org_name !== undefined)            flatUpdates.org_name = updates.org_name;
  if (updates.currency_symbol !== undefined)     flatUpdates.currency_symbol = updates.currency_symbol;
  if (updates.locale !== undefined)              flatUpdates.locale = updates.locale;
  if (updates.primary_colour !== undefined)      flatUpdates.primary_colour = updates.primary_colour;
  if (updates.logo_url !== undefined)            flatUpdates.logo_url = updates.logo_url;
  if (updates.ppe_budget !== undefined)          flatUpdates.monthly_budget_ppe = String(updates.ppe_budget);
  if (updates.consumables_budget !== undefined)  flatUpdates.monthly_budget_consumables = String(updates.consumables_budget);
  if (updates.low_stock_threshold !== undefined) flatUpdates.low_stock_threshold = String(updates.low_stock_threshold);

  return apiFetch<{ message: string }>('/config', {
    method: 'POST',
    body: JSON.stringify({ updates: flatUpdates }),
  });
}

export async function getConfigVersion(): Promise<ApiResponse<{ config_version: number }>> {
  return apiFetch<{ config_version: number }>('/config?version_only=true');
}

// ─── STAFF MANAGEMENT ───────────────────────────────────────────

export async function addStaff(payload: {
  full_name: string; short_name?: string; department_id?: string;
  email?: string; role?: string;
}): Promise<ApiResponse<{ message: string }>> {
  return apiFetch('/config', {
    method: 'POST',
    body: JSON.stringify({ action: 'add_staff', ...payload }),
  });
}

export async function importStaff(staff_list: Array<{
  full_name: string; short_name?: string; department?: string;
  department_id?: string; email?: string; role?: string;
}>): Promise<ApiResponse<{ message: string }>> {
  return apiFetch('/config', {
    method: 'POST',
    body: JSON.stringify({ action: 'import_staff', staff_list }),
  });
}

export async function deactivateStaff(
  staff_id: string
): Promise<ApiResponse<{ message: string }>> {
  return apiFetch('/config', {
    method: 'POST',
    body: JSON.stringify({ action: 'deactivate_staff', staff_id }),
  });
}

// ─── DEPARTMENT MANAGEMENT ──────────────────────────────────────

export async function addDepartment(
  name: string
): Promise<ApiResponse<{ message: string }>> {
  return apiFetch('/config', {
    method: 'POST',
    body: JSON.stringify({ action: 'add_department', name }),
  });
}

export async function deactivateDepartment(
  department_id: string
): Promise<ApiResponse<{ message: string }>> {
  return apiFetch('/config', {
    method: 'POST',
    body: JSON.stringify({ action: 'deactivate_department', department_id }),
  });
}

// ─── CATEGORIES MANAGEMENT ─────────────────────────────────────

export async function updateCategories(
  categories: string[]
): Promise<ApiResponse<{ message: string }>> {
  return apiFetch('/config', {
    method: 'POST',
    body: JSON.stringify({ action: 'update_categories', categories }),
  });
}

// ─── CATEGORY BUDGETS ───────────────────────────────────────────

export async function updateCategoryBudgets(
  budgets: Record<string, number>
): Promise<ApiResponse<{ message: string }>> {
  return apiFetch<{ message: string }>('/config', {
    method: 'POST',
    body: JSON.stringify({ updates: { category_budgets: JSON.stringify(budgets) } }),
  });
}

// ─── ORDERS (paginated — D41) ────────────────────────────────────

export async function getOrders(filters?: OrderFilters): Promise<PaginatedOrdersResponse> {
  const params = new URLSearchParams();
  if (filters?.page)          params.set('page', String(filters.page));
  if (filters?.limit)         params.set('limit', String(filters.limit));
  if (filters?.search)        params.set('search', filters.search);
  if (filters?.status)        params.set('status', filters.status);
  if (filters?.department_id) params.set('department_id', filters.department_id);
  if (filters?.category)      params.set('category', filters.category);
  if (filters?.date_from)     params.set('date_from', filters.date_from);
  if (filters?.date_to)       params.set('date_to', filters.date_to);

  const qs = params.toString();
  const path = qs ? `/orders?${qs}` : '/orders';

  return apiFetch(path) as unknown as Promise<PaginatedOrdersResponse>;
}

export async function createOrder(
  payload: Omit<Order, 'id' | 'created_at' | 'updated_at'>
): Promise<ApiResponse<Order>> {
  return apiFetch<Order>('/orders', {
    method: 'POST',
    body: JSON.stringify({ action: 'new_order', ...payload }),
  });
}

// Optimistic concurrency — pass expected_updated_at to enable conflict detection
export async function updateOrderStatus(
  order_id: string, status: OrderStatus, expected_updated_at?: string
): Promise<ApiResponse<Order>> {
  return apiFetch<Order>('/orders', {
    method: 'POST',
    body: JSON.stringify({ action: 'update_status', order_id, new_status: status, expected_updated_at }),
  });
}

export async function updateOrderField(
  order_id: string, field: string, value: string | number, expected_updated_at?: string
): Promise<ApiResponse<Order>> {
  return apiFetch<Order>('/orders', {
    method: 'POST',
    body: JSON.stringify({ action: 'update_field', order_id, field, value, expected_updated_at }),
  });
}

export async function deleteOrder(
  order_id: string, expected_updated_at?: string
): Promise<ApiResponse<{ message: string }>> {
  return apiFetch<{ message: string }>('/orders', {
    method: 'POST',
    body: JSON.stringify({ action: 'delete_order', order_id, expected_updated_at }),
  });
}

export async function restoreOrder(
  order_id: string
): Promise<ApiResponse<{ message: string }>> {
  return apiFetch<{ message: string }>('/orders', {
    method: 'POST',
    body: JSON.stringify({ action: 'restore_order', order_id }),
  });
}

// ─── ORDER AUDIT LOG ────────────────────────────────────────────

export interface OrderAuditEntry {
  id: string;
  order_id: string;
  action: string;
  field_name: string | null;
  old_value: string | null;
  new_value: string | null;
  performed_by_name: string;
  created_at: string;
}

export async function getOrderAuditLog(
  order_id: string
): Promise<ApiResponse<OrderAuditEntry[]>> {
  return apiFetch<OrderAuditEntry[]>('/orders', {
    method: 'POST',
    body: JSON.stringify({ action: 'get_audit_log', order_id }),
  });
}

export async function markOrderStocked(
  order_id: string
): Promise<ApiResponse<{ message: string }>> {
  return apiFetch<{ message: string }>('/orders', {
    method: 'POST',
    body: JSON.stringify({ action: 'mark_stocked', order_id }),
  });
}

// ─── EXPORT ALL (for reports — bypasses 200-row working-set cap) ─────
//
// Requires a new Edge Function action `export_all` that returns every
// order matching the supplied filter (no pagination). We fall back to
// a batched paginated fetch, but ONLY when the primary call failed
// because the server doesn't ship the action yet. Any other failure
// (expired JWT, 500, offline) is surfaced to the caller — otherwise a
// header-only CSV gets silently downloaded.
//
// The response carries a `truncated` flag in the extra shape when the
// fallback hit its safety cap so the caller can warn the user that the
// CSV doesn't cover every row.

export interface ExportAllResponse extends ApiResponse<Order[]> {
  truncated?: boolean;  // true if the fallback hit its MAX_PAGES cap
  truncated_at?: number; // rows actually returned when truncated
}

// Narrow signals that mean "this server doesn't know about export_all"
// rather than "the server errored" — only these trigger the fallback.
function isUnknownActionError(err: string | undefined): boolean {
  if (!err) return false;
  return err === 'UNKNOWN_ACTION'
      || err === 'INVALID_ACTION'
      || err === 'HTTP 404'
      || err === 'HTTP 405';
}

export async function exportAllOrders(filters?: {
  search?: string; status?: string; department_id?: string;
  category?: string; date_from?: string; date_to?: string;
}): Promise<ExportAllResponse> {
  const first = await apiFetch<Order[]>('/orders', {
    method: 'POST',
    body: JSON.stringify({ action: 'export_all', ...filters }),
  });
  if (first.ok && Array.isArray(first.data)) return first;
  if (first.ok && !Array.isArray(first.data)) {
    // Server returned 200 but with unexpected shape — treat as a real error.
    return { ok: false, error: 'MALFORMED_RESPONSE', message: 'Server returned an unexpected response shape' };
  }
  // Only fall back for "unknown action" responses; propagate everything else.
  if (!isUnknownActionError(first.error)) return first;

  // Fallback: page through /orders at max limit until the server stops giving
  // us more. Cap iterations defensively (MAX_PAGES * LIMIT = 19,800 rows) and
  // flag truncation so the caller can warn the user.
  const LIMIT = 200;
  const MAX_PAGES = 100;
  const all: Order[] = [];
  let lastPage = 0;
  let serverTotalPages = 1;
  for (let page = 1; page <= MAX_PAGES; page++) {
    const r = await getOrders({ ...(filters || {}), page, limit: LIMIT });
    if (!r.ok) {
      if (page === 1) {
        // First page failed — the fallback can't recover; bubble the error up.
        return { ok: false, error: r.error, message: r.message };
      }
      // Partial success — return what we have but mark as truncated.
      return { ok: true, data: all, truncated: true, truncated_at: all.length };
    }
    all.push(...(r.data || []));
    lastPage = page;
    serverTotalPages = r.pagination?.total_pages ?? 1;
    if (page >= serverTotalPages) break;
  }
  const truncated = lastPage < serverTotalPages;
  return { ok: true, data: all, ...(truncated && { truncated: true, truncated_at: all.length }) };
}

// ─── ANALYTICS (server-side aggregation — D42) ───────────────────

export async function fetchAnalytics(filters?: AnalyticsFilters): Promise<AnalyticsResponse> {
  return apiFetch('/orders', {
    method: 'POST',
    body: JSON.stringify({
      action: 'analytics',
      department_id: filters?.department_id || undefined,
      category: filters?.category || undefined,
      date_from: filters?.date_from || undefined,
      date_to: filters?.date_to || undefined,
    }),
  }) as unknown as Promise<AnalyticsResponse>;
}

// ─── INVENTORY ───────────────────────────────────────────────────

export async function getInventory(): Promise<ApiResponse<InventoryData>> {
  return apiFetch<InventoryData>('/inventory');
}

export async function inventoryAdd(payload: {
  item_name: string; sku?: string; size?: string;
  quantity?: number; low_stock_threshold?: number; target_department_id?: string;
}): Promise<ApiResponse<InventoryItem>> {
  return apiFetch<InventoryItem>('/inventory', {
    method: 'POST',
    body: JSON.stringify({ action: 'inventory_add', ...payload }),
  });
}

export async function inventoryIssue(
  inventory_id: string, quantity: number, order_item_id?: string, notes?: string
): Promise<ApiResponse<InventoryItem>> {
  return apiFetch<InventoryItem>('/inventory', {
    method: 'POST',
    body: JSON.stringify({ action: 'inventory_issue', inventory_id, quantity, order_item_id, notes }),
  });
}

export async function inventoryReturn(
  inventory_id: string, quantity: number, notes?: string
): Promise<ApiResponse<InventoryItem>> {
  return apiFetch<InventoryItem>('/inventory', {
    method: 'POST',
    body: JSON.stringify({ action: 'inventory_return', inventory_id, quantity, notes }),
  });
}

export async function inventoryDelete(
  inventory_id: string
): Promise<ApiResponse<{ message: string }>> {
  return apiFetch<{ message: string }>('/inventory', {
    method: 'POST',
    body: JSON.stringify({ action: 'inventory_delete', inventory_id }),
  });
}

export async function inventoryRename(
  inventory_id: string, new_name: string
): Promise<ApiResponse<{ message: string }>> {
  return apiFetch<{ message: string }>('/inventory', {
    method: 'POST',
    body: JSON.stringify({ action: 'inventory_rename', inventory_id, new_name }),
  });
}

export async function inventoryMerge(
  source_id: string, target_id: string
): Promise<ApiResponse<{ message: string; data?: { new_quantity: number } }>> {
  return apiFetch<{ message: string; data?: { new_quantity: number } }>('/inventory', {
    method: 'POST',
    body: JSON.stringify({ action: 'inventory_merge', source_id, target_id }),
  });
}

// ─── USER INVITATIONS ───────────────────────────────────────────

export async function inviteUser(payload: {
  email: string;
  display_name?: string;
  department_ids?: string[];
  role?: string;
}): Promise<ApiResponse<{ message: string }>> {
  const body = {
    ...payload,
    department_id: payload.department_ids?.[0] ?? null,
  };
  return apiFetch("/invite", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// ─── VENDORS ────────────────────────────────────────────────────

export async function getVendors(): Promise<ApiResponse<Vendor[]>> {
  return apiFetch<Vendor[]>('/vendors');
}

export async function addVendor(payload: {
  name: string;
  domain: string;
}): Promise<ApiResponse<{ message: string }>> {
  return apiFetch('/vendors', {
    method: 'POST',
    body: JSON.stringify({ action: 'add_vendor', ...payload }),
  });
}

export async function toggleVendor(
  vendor_id: string, enabled: boolean
): Promise<ApiResponse<{ message: string }>> {
  return apiFetch('/vendors', {
    method: 'POST',
    body: JSON.stringify({ action: 'toggle_vendor', vendor_id, enabled }),
  });
}

export async function deleteVendor(
  vendor_id: string
): Promise<ApiResponse<{ message: string }>> {
  return apiFetch('/vendors', {
    method: 'POST',
    body: JSON.stringify({ action: 'delete_vendor', vendor_id }),
  });
}

// ── CLIENT ONBOARDING (super_admin only) ────────────────────────

export async function onboardClient(payload: {
  company_name: string;
  admin_email: string;
  admin_name?: string;
  max_users: number;
  trial_days?: number;
}): Promise<ApiResponse<{ organisation_id: string; admin_user_id: string; trial_ends_at: string | null }>> {
  return apiFetch('/onboard', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// ── ADMIN (super_admin only) ────────────────────────────────────

export async function adminListOrgs(): Promise<ApiResponse<AdminOrgSummary[]>> {
  return apiFetch<AdminOrgSummary[]>('/admin', {
    method: 'POST',
    body: JSON.stringify({ action: 'list_orgs' }),
  });
}

export async function adminListOrgUsers(
  organisation_id: string
): Promise<ApiResponse<AdminOrgUser[]>> {
  return apiFetch<AdminOrgUser[]>('/admin', {
    method: 'POST',
    body: JSON.stringify({ action: 'list_org_users', organisation_id }),
  });
}

export async function adminChangeUserRole(
  user_id: string, new_role: 'org_admin' | 'dept_admin'
): Promise<ApiResponse<{ message: string }>> {
  return apiFetch<{ message: string }>('/admin', {
    method: 'POST',
    body: JSON.stringify({ action: 'change_user_role', user_id, new_role }),
  });
}

export async function adminDeactivateUser(
  user_id: string
): Promise<ApiResponse<{ message: string }>> {
  return apiFetch<{ message: string }>('/admin', {
    method: 'POST',
    body: JSON.stringify({ action: 'deactivate_user', user_id }),
  });
}

export async function adminReactivateUser(
  user_id: string
): Promise<ApiResponse<{ message: string }>> {
  return apiFetch<{ message: string }>('/admin', {
    method: 'POST',
    body: JSON.stringify({ action: 'reactivate_user', user_id }),
  });
}

export async function adminChangeMaxUsers(
  organisation_id: string, max_users: number
): Promise<ApiResponse<AdminOrgSummary>> {
  return apiFetch<AdminOrgSummary>('/admin', {
    method: 'POST',
    body: JSON.stringify({ action: 'change_max_users', organisation_id, max_users }),
  });
}

export async function adminConvertTrialToActive(
  organisation_id: string
): Promise<ApiResponse<AdminOrgSummary>> {
  return apiFetch<AdminOrgSummary>('/admin', {
    method: 'POST',
    body: JSON.stringify({ action: 'convert_trial_to_active', organisation_id }),
  });
}

export async function adminExtendTrial(
  organisation_id: string, additional_days: number
): Promise<ApiResponse<AdminOrgSummary>> {
  return apiFetch<AdminOrgSummary>('/admin', {
    method: 'POST',
    body: JSON.stringify({ action: 'extend_trial', organisation_id, additional_days }),
  });
}
